import React, { useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import awsmobile from '../aws-exports';
import { get, ApiError } from 'aws-amplify/api';

import './EmployeesRank.css';
import './AssetsRank.css';

Amplify.configure(awsmobile);

// Band targets for a WHOLE period, scaled down by how much of the period has actually
// elapsed. Absolute thresholds cannot work on a total that only accumulates: a fixed
// "Outstanding 150+" is unreachable in week one and unavoidable by week thirteen, so it
// describes the calendar rather than the person. Measured against the real backfill —
// 120 points in 38 of 92 days — a quarter's pace runs to roughly 300, which is where
// these sit. A person holding a steady pace keeps the same band all period, which is
// the property the fixed bands did not have.
const BAND_TARGETS = [
    { min: 300, label: 'Outstanding', color: 'var(--val-exceptional)', bg: 'var(--val-exceptional-bg)' },
    { min: 200, label: 'Strong',      color: 'var(--val-major)',       bg: 'var(--val-major-bg)' },
    { min: 120, label: 'Solid',       color: 'var(--val-solid)',       bg: 'var(--val-solid-bg)' },
    { min: 60,  label: 'Developing',  color: 'var(--val-useful)',      bg: 'var(--val-useful-bg)' },
    { min: 0,   label: 'Light',       color: 'var(--val-routine)',     bg: 'var(--val-routine-bg)' },
];

// Floor on the elapsed fraction. Without it the thresholds collapse toward zero at the
// start of a period and the first person to close a ticket is "Outstanding" — the same
// failure as fixed bands, just inverted. Below roughly two weeks of a quarter there is
// not enough delivered work to band anyone honestly, so hold the day-14 thresholds.
const MIN_ELAPSED = 0.15;

const DAY_MS = 86400000;

// Dates come from caps-periods-dev as plain YYYY-MM-DD, so they are parsed at local
// midnight to match how the header renders them.
function periodProgress(period) {
    if (!period || !period.start_date || !period.end_date) return null;
    const start = Date.parse(`${period.start_date}T00:00:00`);
    const end = Date.parse(`${period.end_date}T23:59:59`);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;

    const span = end - start;
    const elapsed = Date.now() - start;
    return {
        // Clamped at both ends: a period that has not started yet holds the floor, and a
        // period already over stops scaling instead of inflating past its own targets.
        fraction: Math.min(1, Math.max(MIN_ELAPSED, elapsed / span)),
        elapsedDays: Math.min(Math.round(span / DAY_MS), Math.max(0, Math.round(elapsed / DAY_MS))),
        totalDays: Math.round(span / DAY_MS),
    };
}

function scaleBands(fraction) {
    return BAND_TARGETS.map((band) => ({
        ...band,
        // Rounded so the legend shows whole points rather than 123.87.
        min: Math.round(band.min * fraction),
    }));
}

const bandFor = (score, bands) => bands.find((b) => score >= b.min) || bands[bands.length - 1];

// Order is fixed rather than derived from the response so the four dimensions read the
// same way on every row, including rows where one of them is absent.
const DIMENSIONS = ['technical', 'productivity', 'business', 'team'];

// Hardest first, so the eye lands on the substantial work rather than counting up to it.
// Counts of tickets, not points — this says how hard the work was, not what it was worth.
const COMPLEXITY = ['high', 'substantial', 'moderate', 'trivial'];

const AssetsList = () => {

    const [employees, setEmployees] = useState([]);
    // Kept in state, not only pushed to localStorage, because the bands are derived from
    // how far into the period we are.
    const [period, setPeriod] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        async function fetchAssets() {
            try {
                const restOperation = get({
                    apiName: 'employees',
                    path: '/employees',
                    // The positive ranking shares employeesHandler and the /employees path;
                    // adding a dedicated path would mean an API Gateway change, and only
                    // Lambda code deploys in this project.
                    options: { queryParams: { view: 'assets' } },
                });
                const { body } = await restOperation.response;
                const jsonResponse = await body.json();

                // The incident ranking and the asset ranking come out of the same handler
                // on the same path and have the same shape — {id, name, score}. A handler
                // that predates ?view=assets ignores the parameter and answers with
                // severity totals, which this page will happily draw as contributions:
                // identical names, identical numbers, positive labels, "0 tasks" the only
                // tell. Refuse it. Showing nothing is recoverable; showing someone's
                // incident score as their contribution is not.
                if (jsonResponse.view !== 'assets') {
                    setError(
                        'The API answered with the incident ranking, not the asset ranking. ' +
                        'employeesHandler needs to be deployed before this page has data.'
                    );
                    setLoading(false);
                    return;
                }

                // Same contract as the incident ranking: publish the period so
                // AccountabilityHeader can show it when this page is the entry point.
                if (jsonResponse.period) {
                    setPeriod(jsonResponse.period);
                    localStorage.setItem('period', JSON.stringify({
                        start_date: jsonResponse.period.start_date,
                        end_date: jsonResponse.period.end_date,
                    }));
                    window.dispatchEvent(new Event('periodUpdated'));
                }

                setEmployees(jsonResponse.data || []);
                setLoading(false);
            } catch (e) {
                console.log('GET call failed:', e);
                if (e instanceof ApiError && e.response) {
                    const errorResponse = JSON.parse(e.response.body);
                    console.error('Error response:', errorResponse);
                    setError(`Error: ${errorResponse.message}`);
                } else {
                    setError('Failed to fetch the asset ranking');
                }
                setLoading(false);
            }
        }

        fetchAssets();
    }, []);

    const topScore = employees.reduce((max, e) => Math.max(max, Number(e.score) || 0), 0);

    // No period means no way to know how far in we are, so fall back to the full-period
    // targets. That reads harsh rather than generous, which is the safer direction to fail.
    const progress = periodProgress(period);
    const bands = scaleBands(progress ? progress.fraction : 1);

    const header = (
        <div className="page-header">
            {/* Matches the sidebar label exactly, the way the liability page does. The
                name leans on that adjacency: read alone, "asset" suggests a file or a
                domain — and in a media workspace full of logos, OG images and PDFs, that
                reading is a live risk. Next to "Liability Ranking" the accounting sense is
                unmistakable. If the two are ever separated in the nav, this becomes
                "Contribution Ranking". */}
            <h2 className="page-title">Asset Ranking</h2>
            <p className="page-subtitle">
                Total value delivered by each person this period, scored from tickets that
                reached in review, approved, deployed or closed.
                Higher points mean more contribution — rank 1 contributed the most.
            </p>
        </div>
    );

    if (loading) {
        return (
            <>
                {header}
                <p className="loading-state" role="status">Loading ranking…</p>
            </>
        );
    }

    if (error) {
        return (
            <>
                {header}
                <p className="error-state" role="alert">{error}</p>
            </>
        );
    }

    if (!employees.length) {
        return (
            <>
                {header}
                <p className="empty-state">
                    No scored contributions for this period yet. The daily ClickUp sync adds
                    them as tickets are delivered.
                </p>
            </>
        );
    }

    return (
        <div className="employees-container">
            {header}

            <div className="rank-legend">
                <span className="rank-legend-title">Score band</span>
                {bands.map((band, i) => (
                    <span className="rank-legend-item" key={band.label}>
                        <span className="rank-legend-swatch" style={{ background: band.color }} />
                        {band.label}
                        <span style={{ color: 'var(--text-muted)' }}>
                            {/* The bottom band is "under <the band above it>" rather than a
                                hardcoded number, which went stale the moment the thresholds
                                started moving. */}
                            {band.min === 0 ? `(under ${bands[i - 1].min})` : `(${band.min}+)`}
                        </span>
                    </span>
                ))}
            </div>

            {/* Without this the thresholds look arbitrary — they move every day, so the
                legend has to say what they are measured against. */}
            {progress && (
                <p className="rank-legend-note">
                    Thresholds are pro-rated to the period: day {progress.elapsedDays} of{' '}
                    {progress.totalDays}, so they rise as the period runs and a steady pace
                    holds its band. A full period at Outstanding pace is {BAND_TARGETS[0].min} points.
                </p>
            )}

            <ul className="rank-list">
                {employees.map((employee, index) => {
                    const score = Number(employee.score) || 0;
                    const tasks = Number(employee.tasks) || 0;
                    const band = bandFor(score, bands);
                    const dimensions = employee.dimensions || {};
                    const complexity = employee.complexity || {};
                    // Average tier is the honest read on whether a total came from depth or
                    // from volume, and it is the one number the raw score hides.
                    const average = tasks ? (score / tasks).toFixed(1) : '0.0';

                    return (
                        <li key={employee.id}>
                            <div className="asset-row">
                                <div className="rank-row-top">
                                    <span className="rank-position">{index + 1}</span>
                                    <span className="rank-name">{employee.name}</span>
                                    <span className="rank-band" style={{ color: band.color, background: band.bg }}>
                                        {band.label}
                                    </span>
                                    <span className="rank-score">{score} pts</span>
                                </div>
                                <div className="rank-bar-track">
                                    <div
                                        className="rank-bar-fill"
                                        style={{
                                            width: topScore ? `${(score / topScore) * 100}%` : '0%',
                                            background: band.color,
                                        }}
                                    />
                                </div>
                                <div className="asset-meta">
                                    <span className="asset-meta-dimension">
                                        <span className="asset-meta-count">{tasks}</span>
                                        {tasks === 1 ? 'task' : 'tasks'}
                                    </span>
                                    <span className="asset-average">{average} avg tier</span>
                                    {/* These are POINTS, not task counts, and they sum to the
                                        score. Sitting unlabelled next to "22 tasks" they read
                                        as counts — so the group is labelled once rather than
                                        repeating "pts" four times. */}
                                    <span className="asset-meta-label">points by criteria</span>
                                    {DIMENSIONS.filter((d) => dimensions[d]).map((d) => (
                                        <span className="asset-meta-dimension" key={d}>
                                            {d}
                                            <span className="asset-meta-count">{dimensions[d]}</span>
                                        </span>
                                    ))}
                                    {COMPLEXITY.some((c) => complexity[c]) && (
                                        <span className="asset-meta-label">tickets by complexity</span>
                                    )}
                                    {COMPLEXITY.filter((c) => complexity[c]).map((c) => (
                                        <span className="asset-meta-dimension" key={c}>
                                            {c}
                                            <span className="asset-meta-count">{complexity[c]}</span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default AssetsList;
